import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Building, Shield, Clock, ArrowLeft, Edit2, Phone, User, Save, X, Camera } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    department: user?.department || ''
  });

  if (!user) {
    navigate('/login');
    return null;
  }

  const formatDate = (date?: Date) => {
    if (!date) return 'Not available';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement API call to update profile
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      department: user?.department || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Elegant Header */}
      <header className="backdrop-blur-md bg-white/80 border-b border-white/20 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="group flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-all duration-200 hover:bg-white/50 px-3 py-2 rounded-xl"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
                <span className="font-medium">Dashboard</span>
              </button>
              <div className="w-px h-6 bg-slate-200"></div>
              <h1 className="text-2xl font-light text-slate-800 tracking-tight">My Profile</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Elegant Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/20 overflow-hidden">
          {/* Profile Header */}
          <div className="relative p-8 pb-6">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-purple-600/10"></div>
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-6">
                <div className="relative group">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center text-white font-light text-2xl shadow-lg">
                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                  </div>
                  {isEditing && (
                    <button className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-6 h-6 text-white" />
                    </button>
                  )}
                </div>
                <div>
                  <h2 className="text-3xl font-light text-slate-800 mb-2">
                    {isEditing ? (
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={editData.firstName}
                          onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                          className="bg-white/60 border-0 rounded-xl px-4 py-2 text-3xl font-light focus:ring-2 focus:ring-blue-500/20 focus:outline-none backdrop-blur-sm w-40"
                        />
                        <input
                          type="text"
                          value={editData.lastName}
                          onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                          className="bg-white/60 border-0 rounded-xl px-4 py-2 text-3xl font-light focus:ring-2 focus:ring-blue-500/20 focus:outline-none backdrop-blur-sm w-40"
                        />
                      </div>
                    ) : (
                      `${user.firstName} ${user.lastName}`
                    )}
                  </h2>
                  <div className="flex items-center gap-2 text-slate-600 mb-3">
                    <Mail className="w-4 h-4" />
                    <span className="font-medium">{user.email}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 rounded-full text-sm text-slate-700">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="font-medium capitalize">{user.role}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 rounded-full text-sm text-slate-700">
                      <Building className="w-4 h-4 text-indigo-500" />
                      <span className="font-medium">{user.department || 'No department'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <>
                    <button 
                      onClick={handleCancel}
                      className="flex items-center gap-2 px-5 py-2.5 text-slate-600 bg-white/60 rounded-xl hover:bg-white/80 transition-all duration-200 border border-slate-200/50"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                    <button 
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 disabled:opacity-50"
                    >
                      {isSaving ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="px-8 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div className="group">
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  First Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.firstName}
                    onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full bg-white/60 border-0 rounded-xl px-4 py-4 focus:ring-2 focus:ring-blue-500/20 focus:outline-none backdrop-blur-sm transition-all duration-200 text-slate-800"
                  />
                ) : (
                  <div className="p-4 bg-white/40 rounded-xl backdrop-blur-sm border border-white/20 text-slate-800 font-medium">
                    {user.firstName}
                  </div>
                )}
              </div>

              {/* Last Name */}
              <div className="group">
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.lastName}
                    onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full bg-white/60 border-0 rounded-xl px-4 py-4 focus:ring-2 focus:ring-blue-500/20 focus:outline-none backdrop-blur-sm transition-all duration-200 text-slate-800"
                  />
                ) : (
                  <div className="p-4 bg-white/40 rounded-xl backdrop-blur-sm border border-white/20 text-slate-800 font-medium">
                    {user.lastName}
                  </div>
                )}
              </div>

              {/* Email (Read-only) */}
              <div className="group">
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  Email Address
                </label>
                <div className="p-4 bg-slate-50/60 rounded-xl backdrop-blur-sm border border-slate-200/30 text-slate-600 flex items-center justify-between">
                  <span>{user.email}</span>
                  <span className="text-xs text-slate-500 bg-slate-200/50 px-2 py-1 rounded-full">Protected</span>
                </div>
              </div>

              {/* Phone */}
              <div className="group">
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter your phone number"
                    className="w-full bg-white/60 border-0 rounded-xl px-4 py-4 focus:ring-2 focus:ring-blue-500/20 focus:outline-none backdrop-blur-sm transition-all duration-200 text-slate-800"
                  />
                ) : (
                  <div className="p-4 bg-white/40 rounded-xl backdrop-blur-sm border border-white/20 text-slate-800 font-medium">
                    {user.phone || 'Not provided'}
                  </div>
                )}
              </div>

              {/* Department */}
              <div className="group">
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-500" />
                  Department
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.department}
                    onChange={(e) => setEditData(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Enter your department"
                    className="w-full bg-white/60 border-0 rounded-xl px-4 py-4 focus:ring-2 focus:ring-blue-500/20 focus:outline-none backdrop-blur-sm transition-all duration-200 text-slate-800"
                  />
                ) : (
                  <div className="p-4 bg-white/40 rounded-xl backdrop-blur-sm border border-white/20 text-slate-800 font-medium">
                    {user.department || 'Not specified'}
                  </div>
                )}
              </div>

              {/* Role (Read-only) */}
              <div className="group">
                <label className="block text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  Role
                </label>
                <div className="p-4 bg-slate-50/60 rounded-xl backdrop-blur-sm border border-slate-200/30 text-slate-600 flex items-center justify-between">
                  <span className="capitalize font-medium">{user.role}</span>
                  <span className="text-xs text-slate-500 bg-slate-200/50 px-2 py-1 rounded-full">System</span>
                </div>
              </div>
            </div>

            {/* Last Login Info */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-4 h-4" />
                  <span>Last login: {formatDate(user.lastLogin)}</span>
                </div>
                <div className="text-slate-500">
                  Account ID: {user.id?.slice(-8) || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfile;